import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const EMPTY_FORM = {
  id: null,
  tipo_regra: 'historico',
  match_mode: 'exact',
  prioridade: 100,
  historico: '',
  detalhe_historico: '',
  tipo_movimento: 'lancamento',
  tipo: 'despesa',
  direcao_caixa: 'saida',
  conta_origem_id: '',
  conta_destino_id: '',
  transferencia_direcao: 'fixa',
  plano_conta_id: '',
  centro_custo_id: '',
  conta_bancaria_id: '',
  ativo: true,
};

const MapeamentosExtrato = () => {
  const [itens, setItens] = useState([]);
  const [search, setSearch] = useState('');
  const [filtros, setFiltros] = useState({
    tipo: 'todos',
    tipo_movimento: 'todos',
    tipo_regra: 'todos',
    match_mode: 'todos',
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [planosContas, setPlanosContas] = useState([]);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);

  const getPlanoTipoByMovimento = (tipo) => {
    if (tipo === 'patrimonial') return 'patrimonio';
    return tipo;
  };

  const planosFiltrados = useMemo(
    () => planosContas.filter((p) => p.tipo === getPlanoTipoByMovimento(form.tipo)),
    [planosContas, form.tipo]
  );

  const carregar = async () => {
    setCarregando(true);
    try {
      const [mapRs, planosRs, centrosRs, contasRs] = await Promise.all([
        axios.get('/api/historico-mapeamentos', {
          params: {
            search,
            tipo: filtros.tipo !== 'todos' ? filtros.tipo : undefined,
            tipo_movimento: filtros.tipo_movimento !== 'todos' ? filtros.tipo_movimento : undefined,
            tipo_regra: filtros.tipo_regra !== 'todos' ? filtros.tipo_regra : undefined,
            match_mode: filtros.match_mode !== 'todos' ? filtros.match_mode : undefined,
          }
        }),
        axios.get('/api/plano-contas'),
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setItens(Array.isArray(mapRs.data) ? mapRs.data : []);
      setSelectedIds([]);
      setPlanosContas(Array.isArray(planosRs.data) ? planosRs.data : []);
      setCentrosCustos(Array.isArray(centrosRs.data) ? centrosRs.data : []);
      setContasBancarias(Array.isArray(contasRs.data) ? contasRs.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar mapeamentos');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const abrirNovo = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const abrirEditar = (item) => {
    setForm({
      id: item.id,
      tipo_regra: item.tipo_regra || 'historico',
      match_mode: item.match_mode || 'exact',
      prioridade: Number(item.prioridade || 100),
      historico: item.historico_original || item.historico_normalizado || '',
      detalhe_historico: item.detalhe_historico || '',
      tipo_movimento: item.tipo_movimento || 'lancamento',
      tipo: item.tipo || 'despesa',
      direcao_caixa: item.direcao_caixa || (item.tipo === 'patrimonial' ? 'saida' : 'saida'),
      conta_origem_id: item.conta_origem_id || '',
      conta_destino_id: item.conta_destino_id || '',
      transferencia_direcao: item.transferencia_direcao || 'fixa',
      plano_conta_id: item.plano_conta_id || '',
      centro_custo_id: item.centro_custo_id || '',
      conta_bancaria_id: item.conta_bancaria_id || '',
      ativo: item.ativo !== false,
    });
    setShowModal(true);
  };

  const salvar = async () => {
    try {
      if ((form.tipo_regra === 'historico' || form.tipo_regra === 'historico_detalhe') && !form.historico.trim()) {
        return toast.error('Historico obrigatorio para esta regra');
      }
      if ((form.tipo_regra === 'detalhe' || form.tipo_regra === 'historico_detalhe') && !form.detalhe_historico.trim()) {
        return toast.error('Detalhe historico obrigatorio para esta regra');
      }
      if (form.tipo_movimento === 'lancamento' && !form.plano_conta_id) return toast.error('Plano de contas obrigatorio');
      if (form.tipo_movimento === 'lancamento' && form.tipo === 'patrimonial' && !form.direcao_caixa) {
        return toast.error('Direcao do caixa obrigatoria para movimento patrimonial');
      }
      if (form.tipo_movimento === 'transferencia') {
        if (!form.conta_origem_id || !form.conta_destino_id) return toast.error('Conta origem e destino sao obrigatorias');
        if (form.conta_origem_id === form.conta_destino_id) return toast.error('Origem e destino devem ser diferentes');
      }

      const payload = {
        tipo_regra: form.tipo_regra,
        match_mode: form.match_mode,
        prioridade: Number(form.prioridade || 100),
        tipo_movimento: form.tipo_movimento,
        historico: form.historico,
        detalhe_historico: form.detalhe_historico,
        tipo: form.tipo_movimento === 'transferencia' ? 'transferencia' : form.tipo,
        direcao_caixa: form.tipo_movimento === 'lancamento' && form.tipo === 'patrimonial' ? form.direcao_caixa : null,
        plano_conta_id: form.tipo_movimento === 'transferencia' ? null : form.plano_conta_id,
        conta_origem_id: form.tipo_movimento === 'transferencia' ? form.conta_origem_id : null,
        conta_destino_id: form.tipo_movimento === 'transferencia' ? form.conta_destino_id : null,
        transferencia_direcao: form.tipo_movimento === 'transferencia' ? form.transferencia_direcao : 'fixa',
        centro_custo_id: form.tipo === 'patrimonial' ? null : (form.centro_custo_id || null),
        conta_bancaria_id: form.conta_bancaria_id || null,
        ativo: form.ativo,
      };

      if (form.id) {
        await axios.put(`/api/historico-mapeamentos/${form.id}`, payload);
        toast.success('Mapeamento atualizado');
      } else {
        await axios.post('/api/historico-mapeamentos', payload);
        toast.success('Mapeamento criado');
      }
      setShowModal(false);
      await carregar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar mapeamento');
    }
  };

  const remover = async (id) => {
    if (!window.confirm('Deseja desativar este mapeamento?')) return;
    try {
      await axios.delete(`/api/historico-mapeamentos/${id}`);
      toast.success('Mapeamento desativado');
      await carregar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao remover mapeamento');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === itens.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(itens.map((item) => item.id));
    }
  };

  const removerSelecionados = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione ao menos um mapeamento');
      return;
    }
    if (!window.confirm(`Deseja desativar ${selectedIds.length} mapeamentos selecionados?`)) return;
    try {
      await axios.post('/api/historico-mapeamentos/bulk-delete', { ids: selectedIds });
      toast.success(`Mapeamentos desativados: ${selectedIds.length}`);
      await carregar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir selecionados');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mapeamentos de Extrato</h1>
          <p className="text-gray-600">Regras por historico, detalhe e historico + detalhe</p>
        </div>
        <button className="btn-primary" onClick={abrirNovo}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Mapeamento
        </button>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="flex-1">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-input"
                placeholder="Buscar por historico ou detalhe..."
              />
            </div>
            <select className="form-select" value={filtros.tipo_movimento} onChange={(e) => setFiltros({ ...filtros, tipo_movimento: e.target.value })}>
              <option value="todos">Movimento: Todos</option>
              <option value="lancamento">Lançamento</option>
              <option value="transferencia">Transferência</option>
            </select>
            <select className="form-select" value={filtros.tipo} onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}>
              <option value="todos">Tipo: Todos</option>
              <option value="receita">Receita</option>
              <option value="despesa">Despesa</option>
              <option value="patrimonial">Patrimonial</option>
              <option value="transferencia">Transferência</option>
            </select>
            <select className="form-select" value={filtros.tipo_regra} onChange={(e) => setFiltros({ ...filtros, tipo_regra: e.target.value })}>
              <option value="todos">Regra: Todas</option>
              <option value="historico">Histórico</option>
              <option value="detalhe">Detalhe</option>
              <option value="historico_detalhe">Histórico + Detalhe</option>
            </select>
            <select className="form-select" value={filtros.match_mode} onChange={(e) => setFiltros({ ...filtros, match_mode: e.target.value })}>
              <option value="todos">Match: Todos</option>
              <option value="exact">Exato</option>
              <option value="contains">Contém</option>
            </select>
          </div>
          <div className="flex gap-3 mt-3">
            <button className="btn-secondary" onClick={carregar}>
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
              Buscar
            </button>
            <button className="btn-secondary" onClick={() => { setSearch(''); setFiltros({ tipo: 'todos', tipo_movimento: 'todos', tipo_regra: 'todos', match_mode: 'todos' }); }}>
              Limpar filtros
            </button>
            <button className="btn-danger" onClick={removerSelecionados} disabled={selectedIds.length === 0}>
              Excluir selecionados ({selectedIds.length})
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Mapeamentos ({itens.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={itens.length > 0 && selectedIds.length === itens.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Regra</th>
                  <th>Historico</th>
                  <th>Detalhe</th>
                  <th>Tipo</th>
                  <th>Plano de Contas</th>
                  <th>Centro</th>
                  <th>Conta</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelect(item.id)}
                      />
                    </td>
                    <td>
                      <div className="flex flex-col gap-1">
                        <span className="badge badge-info">{item.tipo_regra || 'historico'}</span>
                        <span className="badge badge-secondary">{item.match_mode || 'exact'}</span>
                      </div>
                    </td>
                    <td>{item.historico_original || item.historico_normalizado || '-'}</td>
                    <td>{item.detalhe_historico || '-'}</td>
                    <td>
                      {item.tipo}
                      {item.tipo === 'patrimonial' && item.direcao_caixa ? (
                        <span className="block text-xs text-gray-500">
                          {item.direcao_caixa === 'entrada' ? 'Entrada no caixa' : 'Saida do caixa'}
                        </span>
                      ) : null}
                    </td>
                    <td>{item.plano_conta_codigo} - {item.plano_conta_descricao}</td>
                    <td>{item.centro_custo_codigo ? `${item.centro_custo_codigo} - ${item.centro_custo_descricao}` : '-'}</td>
                    <td>{item.conta_bancaria_banco ? `${item.conta_bancaria_banco} - ${item.conta_bancaria_conta}` : '-'}</td>
                    <td>
                      <div className="flex space-x-2">
                        <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => abrirEditar(item)}>
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button className="text-danger-600 hover:text-danger-900 p-1" onClick={() => remover(item.id)}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {itens.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-gray-500 py-6">
                      {carregando ? (
                        'Carregando...'
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <span>Nenhum mapeamento encontrado</span>
                          <button className="btn-primary" onClick={abrirNovo}>
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Criar primeiro mapeamento
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{form.id ? 'Editar' : 'Novo'} Mapeamento</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Tipo de Regra</label>
                <select className="form-select" value={form.tipo_regra} onChange={(e) => setForm({ ...form, tipo_regra: e.target.value })}>
                  <option value="historico">Apenas historico</option>
                  <option value="detalhe">Apenas detalhe</option>
                  <option value="historico_detalhe">Historico + detalhe</option>
                </select>
              </div>
              <div>
                <label className="form-label">Modo de Comparacao</label>
                <select className="form-select" value={form.match_mode} onChange={(e) => setForm({ ...form, match_mode: e.target.value })}>
                  <option value="exact">Exato</option>
                  <option value="contains">Contem</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="form-label">Historico</label>
                <input
                  className="form-input"
                  value={form.historico}
                  onChange={(e) => setForm({ ...form, historico: e.target.value })}
                  disabled={form.tipo_regra === 'detalhe'}
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Detalhe Historico</label>
                <input
                  className="form-input"
                  value={form.detalhe_historico}
                  onChange={(e) => setForm({ ...form, detalhe_historico: e.target.value })}
                  disabled={form.tipo_regra === 'historico'}
                />
              </div>

              <div>
                <label className="form-label">Prioridade</label>
                <input
                  type="number"
                  min={1}
                  max={999}
                  className="form-input"
                  value={form.prioridade}
                  onChange={(e) => setForm({ ...form, prioridade: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Tipo de Movimento</label>
                <select
                  className="form-select"
                  value={form.tipo_movimento}
                  onChange={(e) => {
                    const tipoMovimento = e.target.value;
                    setForm({
                      ...form,
                      tipo_movimento: tipoMovimento,
                      tipo: tipoMovimento === 'transferencia' ? 'transferencia' : (form.tipo === 'transferencia' ? 'despesa' : form.tipo),
                      plano_conta_id: '',
                      centro_custo_id: tipoMovimento === 'transferencia' ? '' : form.centro_custo_id,
                    });
                  }}
                >
                  <option value="lancamento">Lançamento</option>
                  <option value="transferencia">Transferência</option>
                </select>
              </div>
              <div>
                <label className="form-label">Tipo</label>
                <select
                  className="form-select"
                  value={form.tipo}
                  onChange={(e) => setForm({
                    ...form,
                    tipo: e.target.value,
                    plano_conta_id: '',
                    centro_custo_id: e.target.value === 'patrimonial' ? '' : form.centro_custo_id,
                    direcao_caixa: e.target.value === 'patrimonial' ? (form.direcao_caixa || 'saida') : form.direcao_caixa,
                  })}
                  disabled={form.tipo_movimento === 'transferencia'}
                >
                  <option value="receita">Receita</option>
                  <option value="despesa">Despesa</option>
                  <option value="patrimonial">Patrimonial</option>
                  {form.tipo_movimento === 'transferencia' && <option value="transferencia">Transferência</option>}
                </select>
              </div>
              {form.tipo_movimento === 'lancamento' && form.tipo === 'patrimonial' && (
                <div>
                  <label className="form-label">Direcao do Caixa</label>
                  <select
                    className="form-select"
                    value={form.direcao_caixa}
                    onChange={(e) => setForm({ ...form, direcao_caixa: e.target.value })}
                  >
                    <option value="entrada">Entrada no caixa</option>
                    <option value="saida">Saida do caixa</option>
                  </select>
                </div>
              )}
              <div>
                <label className="form-label">Plano de Contas</label>
                <select className="form-select" value={form.plano_conta_id} onChange={(e) => setForm({ ...form, plano_conta_id: e.target.value })} disabled={form.tipo_movimento === 'transferencia'}>
                  <option value="">{form.tipo_movimento === 'transferencia' ? 'Não se aplica' : 'Selecione'}</option>
                  {planosFiltrados.map((plano) => (
                    <option key={plano.id} value={plano.id}>{plano.codigo} - {plano.descricao}</option>
                  ))}
                </select>
              </div>
              {form.tipo_movimento === 'transferencia' && (
                <>
                  <div>
                    <label className="form-label">Conta Origem</label>
                    <select className="form-select" value={form.conta_origem_id} onChange={(e) => setForm({ ...form, conta_origem_id: e.target.value })}>
                      <option value="">Selecione</option>
                      {contasBancarias.map((conta) => (
                        <option key={`orig-${conta.id}`} value={conta.id}>{conta.banco} - {conta.conta}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Conta Destino</label>
                    <select className="form-select" value={form.conta_destino_id} onChange={(e) => setForm({ ...form, conta_destino_id: e.target.value })}>
                      <option value="">Selecione</option>
                      {contasBancarias.map((conta) => (
                        <option key={`dst-${conta.id}`} value={conta.id}>{conta.banco} - {conta.conta}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Direção da Transferência</label>
                    <select className="form-select" value={form.transferencia_direcao} onChange={(e) => setForm({ ...form, transferencia_direcao: e.target.value })}>
                      <option value="fixa">Fixa (origem para destino)</option>
                      <option value="por_inf_cd">Por Inf. C/D (D origem para destino, C invertido)</option>
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="form-label">Centro de Custo</label>
                <select
                  className="form-select"
                  value={form.centro_custo_id}
                  onChange={(e) => setForm({ ...form, centro_custo_id: e.target.value })}
                  disabled={form.tipo_movimento === 'transferencia' || form.tipo === 'patrimonial'}
                >
                  <option value="">{form.tipo === 'patrimonial' ? 'Nao se aplica' : 'Sem centro'}</option>
                  {centrosCustos.map((centro) => (
                    <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Conta Bancaria</label>
                <select className="form-select" value={form.conta_bancaria_id} onChange={(e) => setForm({ ...form, conta_bancaria_id: e.target.value })}>
                  <option value="">Sem conta</option>
                  {contasBancarias.map((conta) => (
                    <option key={conta.id} value={conta.id}>{conta.banco} - {conta.conta}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={salvar}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapeamentosExtrato;
